import type { SupabaseClient } from '@supabase/supabase-js';

export interface CustomerFormAddress {
  attention: string;
  address: string;
  street2: string;
  suburb: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

export interface CustomerFormCustomer {
  id?: string;
  customer_type?: string;
  status?: string;
  customer_name?: string;
  company_name?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  website?: string;
  contact_persons?: ContactPerson[] | string;
  billing_address?: Partial<CustomerFormAddress>;
  shipping_address?: Partial<CustomerFormAddress>;
  payment_terms?: number;
  credit_limit?: string;
  vat_reg_no?: string;
  vat_treatment?: string;
  is_taxable?: boolean;
  is_pos?: boolean;
  hospitality_notes?: string;
  notes?: string;
  is_own_customer?: boolean;
  territory_name?: string;
  /** Set to 'lead' when the form was populated from a lead pick — controls
   *  whether handleSave inserts a new customer + converts the lead, or
   *  updates the existing customer record. */
  source?: 'customer' | 'lead' | string;
  /** When source === 'lead', the originating lead's id so save can mark it converted. */
  lead_id?: string;
  [key: string]: unknown;
}

export interface ContactPerson {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  designation?: string;
  department?: string;
  is_primary_contact?: boolean;
  contact_person_id?: string;
}

export interface SearchResultCustomer {
  id: string;
  customer_name: string;
  first_name?: string | null;
  last_name?: string | null;
  email: string;
  phone: string | null;
  mobile?: string | null;
  territory_id: string | null;
  customer_type?: string | null;
  status?: string;
  company_name?: string | null;
  is_own_customer?: boolean;
  territory_name?: string | null;
  /** 'customer' or 'lead' — set by search_customers_global RPC */
  source?: 'customer' | 'lead' | string;
  is_pos?: boolean | null;
}

export interface HospProperty {
  id: string;
  property_name: string;
  total_rooms: number | null;
  total_beds: number | null;
  hospitality_tier: string | null;
  default_service_frequency_days: number | null;
  contract_end_date: string | null;
  contract_status: string | null;
}

export interface CustomerFormProps {
  supabase: SupabaseClient;
  onSave: (customer: CustomerFormCustomer) => void;
  onCancel?: () => void;
  initialData?: CustomerFormCustomer | null;
  showPeaceOfSleep?: boolean;
  mode?: 'modal' | 'inline' | 'fullpage';
  globalSearchMode?: boolean;
}

export interface CustomerFormData {
  [key: string]: unknown;
  customer_type: string;
  status: string;
  customer_name: string;
  company_name: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  mobile: string;
  website: string;
  contact_first_name: string;
  contact_last_name: string;
  contact_email: string;
  contact_phone: string;
  contact_mobile: string;
  contact_designation: string;
  contact_department: string;
  billing_address: CustomerFormAddress;
  shipping_address: CustomerFormAddress;
  same_shipping: boolean;
  payment_terms: number;
  credit_limit: string;
  vat_reg_no: string;
  vat_treatment: string;
  is_taxable: boolean;
  is_pos: boolean;
  hospitality_notes: string;
  notes: string;
}

export function createEmptyFormData(defaultCountry: string): CustomerFormData {
  return {
    customer_type: 'individual',
    status: 'active',
    customer_name: '',
    company_name: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    mobile: '',
    website: '',
    contact_first_name: '',
    contact_last_name: '',
    contact_email: '',
    contact_phone: '',
    contact_mobile: '',
    contact_designation: '',
    contact_department: '',
    billing_address: { attention: '', address: '', street2: '', suburb: '', city: '', state: '', zip: '', country: defaultCountry },
    shipping_address: { attention: '', address: '', street2: '', suburb: '', city: '', state: '', zip: '', country: defaultCountry },
    same_shipping: true,
    payment_terms: 0,
    credit_limit: '',
    vat_reg_no: '',
    vat_treatment: '',
    is_taxable: true,
    is_pos: false,
    hospitality_notes: '',
    notes: '',
  };
}
